/**
 * This dialog prompts the user for which type of feedback they would like to provide.
 * Ultimately this directs to either the customer feedback URL or our own feedback form.
 *
 * It the customer has not defined a URL for feedback and there are no extra topics, this
 * dialog is skipped and user is taken directly to the feedback form.
 */

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '@ui/buttons/Button.tsx';
import Radio from '@ui/buttons/Radio.tsx';
import { withModalWindow } from '@ui/modals/ModalWindowMol.tsx';

import { smile, frown } from './faces.tsx';

export type FeedbackQuestion = {
  id: string;
  labelKey: string;
  options: { value: string; labelKey: string }[];
};

export type FeedbackTopic = {
  value: string;
  labelKey: string;
  /** Replaces the satisfied/not satisfied choice in the form. */
  question?: FeedbackQuestion;
};

const Title = styled.h2`
  color: var(--color-primaryText);
  font-weight: 600;
  margin: 0;
  ${({ theme }) => theme.fontSize('20px')};
`;

const StyledLabel = styled.label`
  margin: 0;
  padding-top: 6px;
`;

const NextButtonWrapper = styled.div`
  margin-top: 22px;
`;

const BigButton = styled(Button)`
  ${({ theme }) => theme.borderRadius(8)};
  background-color: var(--color-primaryButton);
  :hover {
    background-color: var(--color-primaryButtonHover);
  }
  color: var(--color-primaryButtonText);
  width: 100%;
  text-align: center;
  padding: 16px 14px;
  font-weight: 500;
  ${({ theme }) => theme.fontSize('16px')};
  line-height: 150%;
`;

const RadioGroup = styled.div`
  margin: 16px 0;
`;

const RadioLine = styled.div`
  margin: 2px 10px;
`;

const ModalText = styled.p`
  font-size: 14px !important;
`;

// Legend styling to visually match existing paragraph style while providing semantic group label
const Legend = styled.legend`
  font-size: 14px;
  margin: 0 0 8px 0;
  padding: 0;
  font-weight: 400;
`;

const FeedbackTextArea = styled.textarea`
  padding: 12px;
  line-height: 1.5 !important;
  width: 100%;
  margin-bottom: 24px !important;
  border-radius: 8px;
  resize: none;
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  margin-bottom: 6px;
`;

const FieldSet = styled.fieldset`
  border: 0;
  margin: 4px 0;
  min-inline-size: 0;
  -webkit-padding-start: 0;
  -webkit-padding-end: 0;
  box-sizing: border-box;
  padding: 0 !important;
`;

const PromptFeedbackType = ({ nextClicked, T, showJourney, extraTopics }) => {
  const [selected, setSelected] = useState('option1');

  return (
    <>
      <RadioGroup>
        <RadioLine>
          <Radio
            id="feedbackTypeMap"
            name="feedbackType"
            value="feedbackTypeMap"
            checked={selected === 'feedbackTypeMap'}
            onChange={e => setSelected(e.target.value)}
            label={T('userFeedback:The map or directions')}
          />
        </RadioLine>

        {showJourney && (
          <RadioLine>
            <Radio
              id="feedbackTypeJourney"
              name="feedbackType"
              value="feedbackTypeJourney"
              checked={selected === 'feedbackTypeJourney'}
              onChange={e => setSelected(e.target.value)}
              label={T('userFeedback:My journey or connection')}
            />
          </RadioLine>
        )}

        {extraTopics.map(topic => (
          <RadioLine key={topic.value}>
            <Radio
              id={topic.value}
              name="feedbackType"
              value={topic.value}
              checked={selected === topic.value}
              onChange={e => setSelected(e.target.value)}
              label={T(topic.labelKey)}
            />
          </RadioLine>
        ))}
      </RadioGroup>

      <NextButtonWrapper>
        <BigButton onClick={() => nextClicked(selected)}>{T('userFeedback:Next')}</BigButton>
      </NextButtonWrapper>
    </>
  );
};

const Thanks = ({ onModalDismiss, T }) => (
  <div>
    <ModalText>
      {T('userFeedback:Your feedback is valuable and helps us improve and create a better application experience')}
    </ModalText>
    <NextButtonWrapper>
      <BigButton
        onClick={() => {
          onModalDismiss();
        }}
      >
        {T('userFeedback:Close')}
      </BigButton>
    </NextButtonWrapper>
  </div>
);

const StyledRadioLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  ${({ $highZoomMaxWidth }) => `
    @media (max-width: ${$highZoomMaxWidth}px) {
      gap: 4px;
      flex-direction: column;
    }
  `}
`;

const StyledRadioLabelText = styled.span`
  text-align: center;
`;

const FeedbackRadioLabel = ({ icon, text, highZoomMaxWidth }) => (
  <StyledRadioLabelContainer $highZoomMaxWidth={highZoomMaxWidth}>
    {icon}
    <StyledRadioLabelText>{text}</StyledRadioLabelText>
  </StyledRadioLabelContainer>
);

const FeedbackQuestionField = ({ question, value, onChange, T }) => (
  <FieldSet>
    <Legend id="feedbackQuestionLabel">{T(question.labelKey)}</Legend>
    {question.options.map(option => (
      <RadioLine key={option.value}>
        <Radio
          id={`feedbackQuestion_${option.value}`}
          name="feedbackQuestion"
          value={option.value}
          checked={value === option.value}
          onChange={e => onChange(e.target.value)}
          label={T(option.labelKey)}
        />
      </RadioLine>
    ))}
  </FieldSet>
);

const FeedbackForm = ({ submitForm, onModalDismiss, T, highZoomMaxWidth, question }) => {
  const [feedbackFeeling, setFeedbackFeeling] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [comments, setComments] = useState('');

  const handleSubmit = e => {
    e.preventDefault();
    submitForm(question ? { answer, comments } : { feeling: feedbackFeeling, comments });
    onModalDismiss();
  };

  return (
    <>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
        {question ? (
          <FeedbackQuestionField question={question} value={answer} onChange={setAnswer} T={T} />
        ) : (
          <FieldSet>
            <Legend id="feedbackFeelingGroupLabel">{T('userFeedback:How is your experience with our product?')}</Legend>
            <RadioButtonsWrapper className="row" role="radiogroup" aria-labelledby="feedbackFeelingGroupLabel">
              <Radio
                id="feedbackFeelingSmile"
                name="feedbackFeeling"
                value="feedbackFeelingSmile"
                aria-label={T('userFeedback:Satisfied')}
                checked={feedbackFeeling === 'feedbackFeelingSmile'}
                color="#565656"
                label={
                  <FeedbackRadioLabel
                    highZoomMaxWidth={highZoomMaxWidth}
                    icon={smile}
                    text={T('userFeedback:Satisfied')}
                  />
                }
                onChange={e => setFeedbackFeeling(e.target.value)}
                buttonStyle
                selectedColor="#0B6623" /* dark green */
                selectedBgColor="#0B6623"
                fullWidth
              />
              <Radio
                id="feedbackFeelingFrown"
                name="feedbackFeeling"
                value="feedbackFeelingFrown"
                aria-label={T('userFeedback:Not Satisfied')}
                checked={feedbackFeeling === 'feedbackFeelingFrown'}
                color="#565656"
                label={
                  <FeedbackRadioLabel
                    highZoomMaxWidth={highZoomMaxWidth}
                    icon={frown}
                    text={T('userFeedback:Not Satisfied')}
                  />
                }
                onChange={e => setFeedbackFeeling(e.target.value)}
                buttonStyle
                selectedColor="#B11224" /* dark red */
                selectedBgColor="#B11224"
                fullWidth
              />
            </RadioButtonsWrapper>
          </FieldSet>
        )}
        <StyledLabel htmlFor="feedbackComments">{T('userFeedback:Your Comments')}</StyledLabel>
        <FeedbackTextArea
          placeholder={T('userFeedback:Share any additional comments or suggestions (optional)')}
          rows="3"
          value={comments}
          onChange={e => setComments(e.target.value)}
          id="feedbackComments"
        />
        <BigButton type="submit">{T('userFeedback:Submit')}</BigButton>
      </form>
    </>
  );
};

const UserFeedbackModal = withModalWindow(
  ({
    T,
    onModalDismiss,
    setModalDismissAction,
    setModalTitle,
    submitForm,
    goCustomerURL, // can be null
    highZoomMaxWidth = 320,
    choseType,
    extraTopics = [] as FeedbackTopic[], // offered after the built-in topics
  }) => {
    useEffect(() => setModalDismissAction(onModalDismiss), []);
    // if true, prompt user for feedback type
    const [promptFeedbackType, setPromptFeedbackType] = useState(!!goCustomerURL || extraTopics.length > 0);
    const [feedbackType, setFeedbackType] = useState(null);
    const [displayThanks, setDisplayThanks] = useState(false); // if true, display thank you

    // Moves focus to the thank-you title so screen readers announce it.
    const focusOnMount = useCallback(node => node && window.requestAnimationFrame(() => node.focus()), []);
    const title = displayThanks
      ? T('userFeedback:Thank you!')
      : promptFeedbackType
        ? T('userFeedback:Select feedback topic')
        : T('userFeedback:Your feedback matters!');
    useLayoutEffect(() => {
      setModalTitle(
        displayThanks ? (
          <Title id="ModalBoxTitle" key="thanks" ref={focusOnMount} tabIndex={-1}>
            {title}
          </Title>
        ) : (
          <Title id="ModalBoxTitle">{title}</Title>
        ),
      );
    }, [title, displayThanks]);

    // Flow is FeedbackType (optional) -> FeedbackForm -> Thanks

    return displayThanks ? (
      <Thanks T={T} onModalDismiss={onModalDismiss} />
    ) : promptFeedbackType ? (
      <PromptFeedbackType
        T={T}
        showJourney={!!goCustomerURL}
        extraTopics={extraTopics}
        nextClicked={selected => {
          choseType(selected);
          if (selected !== 'feedbackTypeJourney') {
            setFeedbackType(selected);
            setPromptFeedbackType(false);
          } else {
            setDisplayThanks(true);
            goCustomerURL();
          }
        }}
      />
    ) : (
      <FeedbackForm
        highZoomMaxWidth={highZoomMaxWidth}
        T={T}
        submitForm={formData => submitForm(formData, feedbackType)}
        question={extraTopics.find(topic => topic.value === feedbackType)?.question}
        onModalDismiss={() => setDisplayThanks(true)}
      />
    );
  },
);

export default UserFeedbackModal;
